import { useState } from 'react';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  createCategory,
  updateCategory,
  deleteCategory,
} from '../api/monday-meeting.api';
import type { MondayMeetingCategory } from '../types/monday-meeting.types';
import './CategoryManager.css';

interface CategoryManagerProps {
  categories: MondayMeetingCategory[];
  onClose: () => void;
  onUpdate: () => void;
}

export function CategoryManager({ categories, onClose, onUpdate }: CategoryManagerProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setError(null);
    setLoading(true);
    try {
      await createCategory(newName.trim());
      setNewName('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (cat: MondayMeetingCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    setError(null);
    setLoading(true);
    try {
      await updateCategory(editingId, { name: editName.trim() });
      setEditingId(null);
      setEditName('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    setDeletingId(id);
    try {
      await deleteCategory(id);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: 'create' | 'edit') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action === 'create') handleCreate();
      else handleSaveEdit();
    }
    if (e.key === 'Escape') {
      if (action === 'edit') {
        setEditingId(null);
        setEditName('');
      }
    }
  };

  return (
    <div className="catmgr-overlay" onClick={onClose}>
      <div className="catmgr-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="catmgr-header">
          <h3>Gérer les sujets</h3>
          <button className="catmgr-close-btn" onClick={onClose}>
            <XMarkIcon />
          </button>
        </div>

        {/* Error */}
        {error && <div className="catmgr-error">{error}</div>}

        {/* Category List */}
        <div className="catmgr-list">
          {categories.map((cat) => (
            <div key={cat.id} className="catmgr-item">
              {editingId === cat.id ? (
                <div className="catmgr-edit-row">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'edit')}
                    className="catmgr-edit-input"
                    autoFocus
                    disabled={loading}
                  />
                  <button
                    className="catmgr-action-btn save"
                    onClick={handleSaveEdit}
                    disabled={loading || !editName.trim()}
                    title="Enregistrer"
                  >
                    <CheckIcon />
                  </button>
                  <button
                    className="catmgr-action-btn cancel"
                    onClick={() => { setEditingId(null); setEditName(''); }}
                    disabled={loading}
                    title="Annuler"
                  >
                    <XMarkIcon />
                  </button>
                </div>
              ) : (
                <>
                  <span className="catmgr-item-name">{cat.name}</span>
                  <div className="catmgr-item-actions">
                    <button
                      className="catmgr-action-btn edit"
                      onClick={() => handleStartEdit(cat)}
                      title="Renommer"
                    >
                      <PencilIcon />
                    </button>
                    <button
                      className="catmgr-action-btn delete"
                      onClick={() => handleDelete(cat.id)}
                      disabled={deletingId === cat.id}
                      title="Supprimer"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          {categories.length === 0 && (
            <div className="catmgr-empty">Aucun sujet défini</div>
          )}
        </div>

        {/* Add New */}
        <div className="catmgr-add">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'create')}
            placeholder="Nouveau sujet..."
            className="catmgr-add-input"
            disabled={loading}
          />
          <button
            className="catmgr-add-btn"
            onClick={handleCreate}
            disabled={loading || !newName.trim()}
          >
            <PlusIcon />
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategoryManager;
